import { useEffect, useRef } from 'react';
import type { DietaryConcernReason } from '../../shared/menu';

interface ReasonPickerProps {
  onPick: (reason: DietaryConcernReason) => void;
  onDismiss: () => void;
}

interface Option {
  reason: DietaryConcernReason;
  label: string;
  hint: string;
}

// Captured once per device on the first strict-tag tap. Drives reason-aware
// phrasing later (medical / celiac / halal / kosher / ethical strict copy on
// the hand-off screen). Non-blocking — dismissing is fine; tags still apply
// strict severity, just without a reason variant in the phrasebook lookup.
const OPTIONS: Option[] = [
  { reason: 'medical', label: 'Medical allergy', hint: 'Reaction risk — anaphylaxis, hospital visit' },
  { reason: 'celiac', label: 'Celiac disease', hint: 'Strict no gluten + cross-contamination' },
  { reason: 'halal', label: 'Halal', hint: 'No pork, no alcohol, halal-slaughtered meat' },
  { reason: 'kosher', label: 'Kosher', hint: 'No pork, no shellfish, kosher rules' },
  { reason: 'ethical', label: 'Ethical or other', hint: 'Vegan, vegetarian, personal — non-medical' },
];

export function ReasonPicker({ onPick, onDismiss }: ReasonPickerProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    queueMicrotask(() => closeRef.current?.focus());
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Why strict?"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onDismiss}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      />
      <div
        style={{
          position: 'relative',
          background: 'var(--color-bg)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: '8px 20px max(20px, env(safe-area-inset-bottom))',
          boxShadow: 'var(--shadow-sheet)',
          maxHeight: '85dvh',
          overflowY: 'auto',
        }}
      >
        <div
          aria-hidden
          style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--color-divider)', margin: '8px auto 16px' }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2, margin: 0, letterSpacing: '-0.01em' }}>
            Why strict?
          </h2>
          <button
            ref={closeRef}
            onClick={onDismiss}
            aria-label="Skip"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 14,
              fontWeight: 500,
              padding: '6px 4px',
            }}
          >
            Skip
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 16px', lineHeight: 1.45 }}>
          The reason shapes how the kitchen reads your request.
        </p>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {OPTIONS.map((opt) => (
            <li key={opt.reason}>
              <button
                onClick={() => onPick(opt.reason)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '1px solid var(--color-divider)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-primary)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  minHeight: 56,
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 600 }}>{opt.label}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                  {opt.hint}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '14px 0 0', lineHeight: 1.5, textAlign: 'center' }}>
          Asked once. Change anytime in Settings.
        </p>
      </div>
    </div>
  );
}
